// Asistente del directorio de clientes (FRA-354, backfill).
//
// La ficha de cliente nace hacia adelante: se crea sola al convertir un lead
// o al dar de alta un evento. Para un venue que ya lleva años operando eso
// deja el directorio vacío y el dato —a quién le vendió, quién le repitió—
// enterrado en los campos sueltos de cada evento.
//
// Aquí se corre en la dirección contraria: de los eventos a las fichas. Se
// agrupan los eventos que parecen ser de la misma persona, y ese agrupado
// ES la detección de recurrentes: un grupo con dos o más eventos es un
// cliente que ya volvió y que hoy nadie ve.
//
// Nada de esto escribe: son funciones puras sobre lo que ya está cargado.
// El asistente enseña cada grupo y el owner confirma.

import {
  buscarCoincidencia,
  coincidenciasDeContacto,
  clienteDesdeEvento,
  limpiaDatosCliente,
  nombreCliente,
  normalizaCorreo,
  normalizaTelefono,
  tieneIdentidad,
} from "./clientes";
import { normalizeText } from "./leadHelpers";

// Un grupo con tres o más nombres distintos casi nunca es una persona con
// tres nombres: es un correo o un teléfono compartido —el del planner que
// reservó para varios clientes, o el de la recepción del venue.
const NOMBRES_DISTINTOS_SOSPECHOSO = 3;

const CORREO_FORMA = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// Lo que la gente teclea cuando el campo es obligatorio y no tiene el dato.
// Agrupar por esto junta a media agenda en una sola ficha.
const LOCALES_BASURA = new Set([
  "no", "sin", "sincorreo", "nocorreo", "na", "none", "ninguno", "nn",
  "test", "prueba", "correo", "email", "x", "xx", "xxx", "asd", "aaa",
]);
const DOMINIOS_BASURA = new Set([
  "example.com", "test.com", "noemail.com", "sincorreo.com", "no.com", "nomail.com",
]);

// El correo sirve para agrupar solo si tiene forma de correo y no es relleno.
export function correoUtilParaAgrupar(v) {
  const c = normalizaCorreo(v);
  if (!c || !CORREO_FORMA.test(c)) return "";
  const [local, dominio] = c.split("@");
  if (LOCALES_BASURA.has(local) || DOMINIOS_BASURA.has(dominio)) return "";
  return c;
}

// Un teléfono mexicano son 10 dígitos. Agrupar es transitivo —si A y B
// comparten teléfono y B y C comparten correo, los tres quedan juntos— y por
// eso aquí se es más estricto que en `buscarCoincidencia`: con 8 dígitos no
// vale la pena la apuesta.
export function telefonoUtilParaAgrupar(v) {
  const t = normalizaTelefono(v);
  if (t.length !== 10) return "";
  if (/^(\d)\1{9}$/.test(t)) return ""; // 0000000000, 5555555555
  return t;
}

// Eventos que podrían tener ficha y no la tienen. Se excluyen los bloqueos
// (no son de nadie), los ya ligados, los descartados a mano y los que no
// traen con qué nombrar a nadie.
export function pendientesDeFicha(events = []) {
  return (events || []).filter(
    (e) =>
      e &&
      e.eventType === "event" &&
      !e.clienteId &&
      !e.sinFichaCliente &&
      tieneIdentidad(clienteDesdeEvento(e))
  );
}

const ms = (v) => {
  if (!v) return 0;
  if (typeof v.toMillis === "function") return v.toMillis();
  if (typeof v.toDate === "function") return v.toDate().getTime();
  if (v instanceof Date) return v.getTime();
  if (typeof v.seconds === "number") return v.seconds * 1000;
  const d = new Date(v);
  return isNaN(d) ? 0 : d.getTime();
};

function unionFind() {
  const padre = new Map();
  const find = (x) => {
    if (!padre.has(x)) padre.set(x, x);
    let raiz = x;
    while (padre.get(raiz) !== raiz) raiz = padre.get(raiz);
    let cur = x;
    while (padre.get(cur) !== cur) {
      const sig = padre.get(cur);
      padre.set(cur, raiz);
      cur = sig;
    }
    return raiz;
  };
  const union = (a, b) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) padre.set(ra, rb);
  };
  return { find, union };
}

// Qué tan completa viene la ficha que se puede sacar de un evento. Se
// prefiere el evento con más datos, no el más reciente: el más nuevo bien
// puede ser el que se capturó a las carreras.
const PESOS = { correo: 3, telefono: 3, nombre: 2, nombreEmpresa: 2, apellido: 1, rfc: 1 };
const puntajeFicha = (f) =>
  Object.entries(PESOS).reduce((s, [campo, peso]) => s + (f[campo] ? peso : 0), 0);

const CAMPOS_FUSIONABLES = ["nombre", "apellido", "nombreEmpresa", "correo", "telefono", "rfc"];

// La ficha propuesta es la unión de lo que traen todos los eventos del
// grupo: se parte del más completo y cada hueco se llena con el primero que
// lo tenga. Así un evento que solo trajo teléfono y otro que solo trajo
// correo producen una ficha con los dos.
function fusionaFichas(entradas) {
  const ordenadas = entradas
    .slice()
    .sort((a, b) => b.puntaje - a.puntaje || b.fecha - a.fecha);
  const base = { ...ordenadas[0].ficha };
  ordenadas.slice(1).forEach(({ ficha }) => {
    CAMPOS_FUSIONABLES.forEach((campo) => {
      if (!base[campo] && ficha[campo]) base[campo] = ficha[campo];
    });
  });
  const esMoral = entradas.some((e) => e.ficha.clientType === "moral") && !!base.nombreEmpresa;
  return limpiaDatosCliente({ ...base, clientType: esMoral ? "moral" : "fisica" });
}

// Los valores distintos que aparecen entre los eventos del grupo. Si hay más
// de uno el asistente los ofrece como opciones en vez de elegir por su
// cuenta: cuál correo es el bueno lo sabe el owner, no nosotros.
function variantesDe(entradas, campo) {
  const vistos = new Map();
  entradas.forEach(({ ficha }) => {
    const v = String(ficha[campo] || "").trim();
    const clave = v.toLowerCase();
    if (v && !vistos.has(clave)) vistos.set(clave, v);
  });
  return [...vistos.values()];
}

// Nombre comparable. Un nombre suelto y corto ("Ana") no identifica a nadie,
// así que no se usa para sugerir nada: se exige nombre y apellido, o una
// razón social con cuerpo.
export function nombreComparable(c) {
  const n = normalizeText(nombreCliente(c)).replace(/\s+/g, " ").trim();
  // Sin persona, el nombre es la empresa: «Sin empresa» tampoco cuenta aquí.
  if (!n || n === "cliente" || EMPRESAS_GENERICAS.has(n)) return "";
  if (n.split(" ").length < 2 && n.length < 8) return "";
  return n;
}

// ¿Esta ficha propuesta ya existe en el directorio? Correo y teléfono son
// certeza (los resuelve `buscarCoincidencia`); el nombre es sospecha, y se
// devuelve marcado como tal para que la UI pregunte en vez de afirmar.
export function fichaParecida(datos, clientes = []) {
  const exacta = buscarCoincidencia(clientes, datos);
  if (exacta) {
    const mail = normalizaCorreo(datos.correo);
    const porCorreo = !!mail && normalizaCorreo(exacta.correo) === mail;
    return { cliente: exacta, motivo: porCorreo ? "correo" : "telefono", seguro: true };
  }
  const activos = (clientes || []).filter((c) => c && !c.archivedAt);
  const n = nombreComparable(datos);
  if (n) {
    const porNombre = activos.find((c) => nombreComparable(c) === n);
    if (porNombre) return { cliente: porNombre, motivo: "nombre", seguro: false };
  }
  // La empresa también cuenta, aunque la ficha esté como persona física:
  // una ficha nacida de un lead se queda "física" con la empresa anotada, y
  // el grupo moral con la misma razón social no la encontraba por nombre.
  const emp = empresaComparable(datos);
  if (emp) {
    const porEmpresa = activos.find((c) => empresaComparable(c) === emp);
    if (porEmpresa) return { cliente: porEmpresa, motivo: "empresa", seguro: false };
  }
  return null;
}

// Lo que la gente escribe en «empresa» cuando no hay empresa. Comparado tal
// cual, «Sin empresa» juntaba a todos los que lo anotaron.
const EMPRESAS_GENERICAS = new Set([
  "sin empresa",
  "ninguna",
  "ninguno",
  "no aplica",
  "n/a",
  "na",
  "no",
  "particular",
  "persona fisica",
  "independiente",
  "otro",
  "otra",
]);

// Razón social comparable, con el mismo umbral de cuerpo que el nombre.
export function empresaComparable(c) {
  const n = normalizeText(c?.nombreEmpresa || "").replace(/\s+/g, " ").trim();
  if (!n || EMPRESAS_GENERICAS.has(n)) return "";
  if (n.split(" ").length < 2 && n.length < 8) return "";
  return n;
}

// Nombre y apellido juntos, solo si están los dos: «Ana» sola no identifica
// a nadie para avisar de un duplicado.
function personaComparable(c) {
  const nombre = normalizeText(c?.nombre || "").replace(/\s+/g, " ").trim();
  const apellido = normalizeText(c?.apellido || "").replace(/\s+/g, " ").trim();
  if (!nombre || !apellido) return "";
  return `${nombre} ${apellido}`;
}

// Al dar de alta un cliente: quiénes del directorio podrían ser el mismo, y
// por qué (correo, teléfono, nombre y apellido, empresa). No bloquea: avisa,
// y quien captura decide si usa el que existe o crea uno nuevo.
export function parecidosAlAlta(datos = {}, clientes = []) {
  const activos = (clientes || []).filter((c) => c && !c.archivedAt);
  const porId = new Map();
  const marcar = (c, motivo) => {
    const prev = porId.get(c.id) || { cliente: c, motivos: [] };
    if (!prev.motivos.includes(motivo)) prev.motivos.push(motivo);
    porId.set(c.id, prev);
  };
  const mail = normalizaCorreo(datos.correo);
  if (mail) coincidenciasDeContacto(activos, { correo: datos.correo }).forEach((c) => marcar(c, "correo"));
  if (normalizaTelefono(datos.telefono).length >= 8) {
    coincidenciasDeContacto(activos, { telefono: datos.telefono }).forEach((c) => marcar(c, "teléfono"));
  }
  const persona = personaComparable(datos);
  if (persona) activos.filter((c) => personaComparable(c) === persona).forEach((c) => marcar(c, "nombre y apellido"));
  const emp = empresaComparable(datos);
  if (emp) activos.filter((c) => empresaComparable(c) === emp).forEach((c) => marcar(c, "empresa"));
  return [...porId.values()];
}

// El corazón del asistente: de los eventos sueltos a personas.
// Dos pasadas. La primera junta por contacto —correo y teléfono, que son
// certeza—. La segunda junta además por nombre completo, que es sospecha: no
// todos los venues capturaron correo o teléfono, y sin esta pasada el
// asistente no encuentra un solo recurrente en un venue que solo anotó
// nombres. Los grupos que existen únicamente gracias al nombre se marcan
// `porNombre` y SIEMPRE pasan por revisión manual: nunca se crean en lote.
function agrupaPorClaves(pendientes, usarNombre) {
  const { find, union } = unionFind();
  const primerEventoPorClave = new Map();
  pendientes.forEach((e) => {
    find(e.id);
    const ficha = clienteDesdeEvento(e);
    const claves = [];
    const correo = correoUtilParaAgrupar(ficha.correo);
    if (correo) claves.push(`c:${correo}`);
    const telefono = telefonoUtilParaAgrupar(ficha.telefono);
    if (telefono) claves.push(`t:${telefono}`);
    if (usarNombre) {
      const nombre = nombreComparable(ficha);
      if (nombre) claves.push(`n:${nombre}`);
    }
    claves.forEach((clave) => {
      if (primerEventoPorClave.has(clave)) union(e.id, primerEventoPorClave.get(clave));
      else primerEventoPorClave.set(clave, e.id);
    });
  });
  const cubos = new Map();
  pendientes.forEach((e) => {
    const raiz = find(e.id);
    if (!cubos.has(raiz)) cubos.set(raiz, []);
    cubos.get(raiz).push(e);
  });
  return { cubos, raizDe: (id) => find(id) };
}

export function agruparEventosSinFicha(events = [], clientes = []) {
  const pendientes = pendientesDeFicha(events);
  const porContacto = agrupaPorClaves(pendientes, false);
  const { cubos } = agrupaPorClaves(pendientes, true);

  const grupos = [...cubos.values()].map((eventos) => {
    const entradas = eventos.map((e) => {
      const ficha = clienteDesdeEvento(e);
      return { evento: e, ficha, puntaje: puntajeFicha(ficha), fecha: ms(e.startTime) };
    });
    const datos = fusionaFichas(entradas);
    const nombresDistintos = new Set(
      entradas.map(({ ficha }) => nombreComparable(ficha)).filter(Boolean)
    );
    const ordenados = entradas
      .slice()
      .sort((a, b) => b.fecha - a.fecha)
      .map(({ evento }) => ({
        id: evento.id,
        nombre: evento.nombre || "Sin nombre",
        startTime: evento.startTime,
        status: evento.status || "",
        cliente: nombreCliente(clienteDesdeEvento(evento)),
        correo: evento.correo || "",
        telefono: evento.telefono || "",
      }));
    // El grupo existe solo por el nombre si sus eventos venían separados
    // cuando únicamente se miraban correo y teléfono.
    const raicesPorContacto = new Set(eventos.map((e) => porContacto.raizDe(e.id)));
    const porNombre = raicesPorContacto.size > 1;
    return {
      // Id estable entre recálculos: el evento más viejo del grupo.
      id: entradas.slice().sort((a, b) => a.fecha - b.fecha)[0].evento.id,
      eventos: ordenados,
      datos,
      variantes: {
        correo: variantesDe(entradas, "correo"),
        telefono: variantesDe(entradas, "telefono"),
        nombreEmpresa: variantesDe(entradas, "nombreEmpresa"),
      },
      nombresDistintos: [...nombresDistintos],
      esRecurrente: eventos.length > 1,
      porNombre,
      // Sospechoso por dos caminos: varios nombres bajo un mismo contacto, o
      // un montón de eventos juntados solo porque el nombre coincide
      // ("Contacto Corporativo" rara vez es una persona).
      sospechoso:
        nombresDistintos.size >= NOMBRES_DISTINTOS_SOSPECHOSO ||
        (porNombre && eventos.length >= NOMBRES_DISTINTOS_SOSPECHOSO + 1),
      fichaExistente: fichaParecida(datos, clientes),
    };
  });

  // Primero lo que pide criterio —recurrentes, sospechosos, los que quizá ya
  // tienen ficha—, y dentro de eso los de más eventos. Los de un solo evento
  // se despachan en lote al final.
  return grupos.sort((a, b) => {
    const pesoA = (a.sospechoso ? 2 : 0) + (a.esRecurrente ? 1 : 0);
    const pesoB = (b.sospechoso ? 2 : 0) + (b.esRecurrente ? 1 : 0);
    if (pesoA !== pesoB) return pesoB - pesoA;
    if (a.eventos.length !== b.eventos.length) return b.eventos.length - a.eventos.length;
    return nombreCliente(a.datos).localeCompare(nombreCliente(b.datos), "es", { sensitivity: "base" });
  });
}

// Los que necesitan que alguien los mire uno por uno.
export const requiereRevision = (g) => !!(g.esRecurrente || g.sospechoso || g.porNombre || g.fichaExistente);

// El resumen cuenta las dos cosas —clientes y eventos— porque en la portada
// conviven: quien lee "92 eventos" y luego "56 clientes" necesita ver a dónde
// se fueron los 92, o parece que se perdieron.
export function resumenBackfill(grupos = []) {
  const revisables = grupos.filter(requiereRevision);
  const sueltos = grupos.filter((g) => !requiereRevision(g));
  const cuentaEventos = (lista) => lista.reduce((s, g) => s + g.eventos.length, 0);
  const recurrentes = grupos.filter((g) => g.esRecurrente);
  return {
    eventos: cuentaEventos(grupos),
    personas: grupos.length,
    recurrentes: recurrentes.length,
    eventosRecurrentes: cuentaEventos(recurrentes),
    sospechosos: grupos.filter((g) => g.sospechoso).length,
    porNombre: grupos.filter((g) => g.porNombre).length,
    conFicha: grupos.filter((g) => g.fichaExistente).length,
    revisables: revisables.length,
    eventosRevisables: cuentaEventos(revisables),
    sueltos: sueltos.length,
    eventosSueltos: cuentaEventos(sueltos),
  };
}

// ── Duplicados entre fichas que YA existen ───────────────────────────
//
// Otro problema, distinto del backfill: la misma persona capturada dos veces
// —una con correo y otra solo con teléfono, o con un acento de diferencia—
// antes de que existiera el asistente. Aquí solo se encuentran y se enseñan;
// fusionarlas es trabajo aparte y a mano.

function motivoDuplicado(a, b) {
  const correoA = correoUtilParaAgrupar(a.correo);
  if (correoA && correoA === correoUtilParaAgrupar(b.correo)) return "correo";
  const telA = telefonoUtilParaAgrupar(a.telefono);
  if (telA && telA === telefonoUtilParaAgrupar(b.telefono)) return "telefono";
  const nombreA = nombreComparable(a);
  if (nombreA && nombreA === nombreComparable(b)) return "nombre";
  return null;
}

export function posiblesDuplicados(clientes = []) {
  const activos = (clientes || []).filter((c) => c && !c.archivedAt);
  const pares = [];
  for (let i = 0; i < activos.length; i += 1) {
    for (let j = i + 1; j < activos.length; j += 1) {
      const motivo = motivoDuplicado(activos[i], activos[j]);
      if (motivo) pares.push({ a: activos[i], b: activos[j], motivo });
    }
  }
  return pares;
}
